import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/EditOutlined';
import FlecheHautIcon from '@mui/icons-material/ArrowUpward';
import FlecheBasIcon from '@mui/icons-material/ArrowDownward';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FavoriIcon from '@mui/icons-material/Star';
import NonFavoriIcon from '@mui/icons-material/StarBorder';
import { useSearchParams } from 'react-router-dom';

import { listerPlanning, type SeancePlanifiee } from '../Planning/planningApi.ts';
import { useEtatSession } from '../useEtatSession.ts';
import { alignerEtapes, calculerHoraire, formaterDureeHm, formaterHeure, versMinutes, type Horaire } from './calculMatinee.ts';
import {
  enregistrerMatinee, lireMatinee, reinitialiserMatinee,
  type Etape, type Matinee, type Scenario, type TypeEtape,
} from './matineeApi.ts';

const TYPES: { valeur: TypeEtape; libelle: string }[] = [
  { valeur: 'NORMALE', libelle: 'Durée fixe' },
  { valeur: 'SEANCE', libelle: 'Séance' },
  { valeur: 'COURUE', libelle: 'Trajet en courant' },
  { valeur: 'DOUCHE', libelle: 'Douche (référence)' },
];

// AAAA-MM-JJ de demain (fuseau local) : on prepare son reveil la veille.
function demain(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Seances du jour qui ont une duree (ni repos ni note).
function seancesAvecDuree(liste: SeancePlanifiee[]): SeancePlanifiee[] {
  return liste
    .filter((p) => p.categorie !== 'REPOS' && p.categorie !== 'NOTE' && p.dureeTotaleS)
    .sort((a, b) => a.ordre - b.ordre);
}

const cloner = (m: Matinee): Matinee => JSON.parse(JSON.stringify(m));

/**
 * Heure de lever : pour chaque scenario de sortie (gym, course dehors...),
 * calcule a rebours depuis l'heure de fin de douche l'heure a laquelle se
 * lever, a partir de la duree de la seance du jour.
 */
export default function HeureLever() {
  const [config, setConfig] = React.useState<Matinee | null>(null);
  const [erreur, setErreur] = React.useState<string | null>(null);
  // Ouverture depuis le detail d'une seance du calendrier :
  // /lever?date=AAAA-MM-JJ&seance=id&duree=minutes (duree : si la seance
  // n'est pas dans le calendrier de l'utilisateur, ex : coach chez un athlete).
  const [params] = useSearchParams();
  const demande = React.useRef({ date: params.get('date'), seance: params.get('seance'), duree: Number(params.get('duree')) || null });
  const [date, setDate] = useEtatSession<string>('matinee.date', demain());
  React.useEffect(() => {
    const d = params.get('date');
    if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) setDate(d);
  }, [params, setDate]);
  // Duree de la seance (HH:MM) : celle du calendrier, modifiable.
  const [duree, setDuree] = useEtatSession<string>('matinee.duree', '01:05');
  const [finDouche, setFinDouche] = React.useState('07:50');
  const [seances, setSeances] = React.useState<SeancePlanifiee[]>([]);
  const [idSeance, setIdSeance] = React.useState<string | null>(null);
  // Brouillon des scenarios en cours de modification (null hors edition).
  const [brouillon, setBrouillon] = React.useState<Matinee | null>(null);
  const [enCours, setEnCours] = React.useState(false);

  React.useEffect(() => {
    lireMatinee()
      .then((m) => {
        setConfig(m);
        setFinDouche(m.finDouche);
      })
      .catch((e: Error) => setErreur(e.message));
  }, []);

  // Seances posees ce jour-la : la premiere fixe la duree.
  React.useEffect(() => {
    if (!date) return;
    listerPlanning(date, date)
      .then((liste) => {
        const avecDuree = seancesAvecDuree(liste);
        setSeances(avecDuree);
        // Seance demandee (lien du calendrier), une seule fois.
        const { date: dateDemandee, seance, duree: dureeDemandee } = demande.current;
        if (date === dateDemandee && (seance || dureeDemandee)) {
          const choisie = avecDuree.find((p) => p.id === seance);
          if (choisie || dureeDemandee) {
            demande.current = { date: null, seance: null, duree: null };
            setIdSeance(choisie ? choisie.id : null);
            setDuree(formaterDureeHm(choisie ? Math.round(choisie.dureeTotaleS! / 60) : dureeDemandee!));
            return;
          }
        }
        if (avecDuree.length) {
          setIdSeance(avecDuree[0].id);
          setDuree(formaterDureeHm(Math.round(avecDuree[0].dureeTotaleS! / 60)));
        } else {
          setIdSeance(null);
        }
      })
      .catch(() => setSeances([]));
  }, [date, setDuree]);

  const choisirSeance = (p: SeancePlanifiee) => {
    setIdSeance(p.id);
    setDuree(formaterDureeHm(Math.round(p.dureeTotaleS! / 60)));
  };

  const finDoucheMin = versMinutes(finDouche);
  const dureeMin = versMinutes(duree);

  // Fin de douche : enregistree des qu'elle change (hors edition des scenarios).
  const enregistrerFinDouche = async () => {
    if (!config || finDoucheMin === null || finDouche === config.finDouche) return;
    try {
      const m = await enregistrerMatinee({ ...config, finDouche });
      setConfig(m);
      if (brouillon) setBrouillon({ ...brouillon, finDouche });
    } catch (e) {
      setErreur((e as Error).message);
    }
  };

  const scenarios = (brouillon ?? config)?.scenarios ?? [];
  const horaires: (Horaire | null)[] = scenarios.map((s) =>
    finDoucheMin === null || dureeMin === null ? null : calculerHoraire(s, finDoucheMin, dureeMin));
  // Lignes communes des tableaux : memes etapes sur la meme ligne.
  const alignement = React.useMemo(() => alignerEtapes(scenarios), [scenarios]);
  // Le lever le plus tardif (le plus de sommeil) est mis en avant.
  const leverMax = Math.max(...horaires.map((h) => (h ? h.leverMin : -Infinity)));

  const enregistrerBrouillon = async () => {
    if (!brouillon) return;
    setEnCours(true);
    try {
      const m = await enregistrerMatinee({ ...brouillon, finDouche: finDoucheMin === null ? brouillon.finDouche : finDouche });
      setConfig(m);
      setBrouillon(null);
      setErreur(null);
    } catch (e) {
      setErreur((e as Error).message);
    } finally {
      setEnCours(false);
    }
  };

  const reinitialiser = async () => {
    if (!window.confirm('Revenir aux quatre scénarios par défaut ? Tes modifications seront perdues.')) return;
    try {
      const m = await reinitialiserMatinee();
      setConfig(m);
      setFinDouche(m.finDouche);
      setBrouillon(null);
      setErreur(null);
    } catch (e) {
      setErreur((e as Error).message);
    }
  };

  // Favori (un seul, ou aucun) : enregistre aussitot ; son heure de lever
  // est affichee sur l'accueil.
  const basculerFavori = async (i: number) => {
    if (!config) return;
    const favori = !config.scenarios[i].favori;
    try {
      const m = await enregistrerMatinee({
        ...config,
        scenarios: config.scenarios.map((s, j) => ({ ...s, favori: favori && j === i })),
      });
      setConfig(m);
    } catch (e) {
      setErreur((e as Error).message);
    }
  };

  // Modification d'un scenario du brouillon.
  const modifierScenario = (i: number, maj: (s: Scenario) => Scenario) => {
    if (!brouillon) return;
    setBrouillon({ ...brouillon, scenarios: brouillon.scenarios.map((s, j) => (j === i ? maj(s) : s)) });
  };

  const ajouterScenario = () => {
    if (!brouillon) return;
    setBrouillon({
      ...brouillon,
      scenarios: [...brouillon.scenarios, {
        nom: 'Nouveau scénario',
        margeMin: 0,
        etapes: [
          { libelle: 'Départ', dureeMin: 20, type: 'NORMALE' },
          { libelle: 'Exercice', dureeMin: 0, type: 'SEANCE' },
          { libelle: 'Douche', dureeMin: 10, type: 'DOUCHE' },
        ],
      }],
    });
  };

  return (
    <Box component="main" sx={{ p: { xs: 2, md: 6 }, pt: { xs: 10, md: 10 } }}>
      <Typography sx={{ mb: 3 }} color="text.secondary">
        Pour chaque façon de faire ta séance, l'heure à laquelle te lever pour avoir fini ta douche à l'heure voulue.
        La durée de la séance vient de ton calendrier ; tu peux l'ajuster.
      </Typography>

      {erreur && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErreur(null)}>{erreur}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Jour"
          type="date"
          size="small"
          value={date}
          onChange={(ev) => setDate(ev.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Fin de douche"
          type="time"
          size="small"
          value={finDouche}
          onChange={(ev) => setFinDouche(ev.target.value)}
          onBlur={enregistrerFinDouche}
          error={finDoucheMin === null}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 140 }}
        />
        <TextField
          label="Durée de la séance"
          type="time"
          size="small"
          value={duree}
          onChange={(ev) => { setDuree(ev.target.value); setIdSeance(null); }}
          error={dureeMin === null}
          helperText="hh:mm"
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 300 }}
          sx={{ width: 140 }}
        />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {seances.length === 0 && (
            <Typography variant="body2" color="text.secondary">Aucune séance au calendrier ce jour-là.</Typography>
          )}
          {seances.map((p) => (
            <Chip
              key={p.id}
              label={`${p.nom} · ${formaterDureeHm(Math.round(p.dureeTotaleS! / 60))}`}
              color={p.id === idSeance ? 'primary' : 'default'}
              variant={p.id === idSeance ? 'filled' : 'outlined'}
              onClick={() => choisirSeance(p)}
            />
          ))}
        </Box>
        <Box sx={{ flex: 1 }} />
        {brouillon ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setBrouillon(null)} disabled={enCours}>Annuler</Button>
            <Button variant="contained" onClick={enregistrerBrouillon} disabled={enCours}>Enregistrer</Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {config && !config.parDefaut && (
              <Tooltip title="Revenir aux scénarios par défaut">
                <IconButton onClick={reinitialiser}><RestartAltIcon /></IconButton>
              </Tooltip>
            )}
            <Button startIcon={<EditIcon />} onClick={() => config && setBrouillon(cloner(config))} disabled={!config}>
              Modifier les scénarios
            </Button>
          </Box>
        )}
      </Paper>

      {/* L'editeur d'etapes a besoin de cartes plus larges (libelle, type, duree). */}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: `repeat(auto-fill, minmax(${brouillon ? 'min(100%, 440px)' : '280px'}, 1fr))`, alignItems: 'start' }}>
        {scenarios.map((s, i) => (
          brouillon
            ? <EditeurScenario
                key={i}
                scenario={s}
                horaire={horaires[i]}
                onChange={(maj) => modifierScenario(i, () => maj)}
                onSupprimer={brouillon.scenarios.length > 1
                  ? () => setBrouillon({ ...brouillon, scenarios: brouillon.scenarios.filter((_, j) => j !== i) })
                  : undefined}
                onDeplacer={(sens) => {
                  const j = i + sens;
                  if (j < 0 || j >= brouillon.scenarios.length) return;
                  const liste = [...brouillon.scenarios];
                  [liste[i], liste[j]] = [liste[j], liste[i]];
                  setBrouillon({ ...brouillon, scenarios: liste });
                }}
              />
            : <CarteScenario
                key={i}
                scenario={s}
                horaire={horaires[i]}
                lignes={alignement[i]}
                plusTard={horaires[i]?.leverMin === leverMax && scenarios.length > 1}
                onFavori={() => basculerFavori(i)}
              />
        ))}
        {brouillon && brouillon.scenarios.length < 10 && (
          <Button variant="outlined" startIcon={<AddIcon />} onClick={ajouterScenario} sx={{ minHeight: 80 }}>
            Ajouter un scénario
          </Button>
        )}
      </Box>
    </Box>
  );
}

// Lignes sur une seule ligne de texte : meme hauteur dans tous les tableaux.
const cellule = { py: 0.5, px: 1, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', height: 33 } as const;

// Ligne du tableau horaire, comme dans la feuille : libelle, duree, heure de fin.
function LigneHoraireTable({ libelle, duree, heure, type }: { libelle: string; duree?: string; heure: string; type?: TypeEtape | null }) {
  const fort = type === 'SEANCE' || type === 'DOUCHE' || type === undefined;
  return (
    <TableRow sx={type === 'SEANCE' ? { bgcolor: 'action.hover' } : undefined}>
      <TableCell sx={{ ...cellule, fontWeight: fort ? 600 : 400 }}>
        {libelle}{type === 'COURUE' && <Typography component="span" variant="caption" color="text.secondary"> (en courant)</Typography>}
      </TableCell>
      <TableCell sx={cellule} align="right">{duree ?? ''}</TableCell>
      <TableCell sx={{ ...cellule, fontWeight: fort ? 600 : 400 }} align="right">{heure}</TableCell>
    </TableRow>
  );
}

// lignes : index de l'etape sur chaque ligne commune (null : ligne vide), cf alignerEtapes.
function CarteScenario({ scenario, horaire, lignes, plusTard, onFavori }: {
  scenario: Scenario;
  horaire: Horaire | null;
  lignes: (number | null)[];
  plusTard: boolean;
  onFavori: () => void;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: plusTard ? 'primary.main' : undefined, borderWidth: plusTard ? 2 : 1 }}>
      {/* En-tete de hauteur fixe : les tableaux commencent a la meme hauteur. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, height: 28 }}>
        <Tooltip title={scenario.favori ? "Favori : heure de lever affichée sur l'accueil" : 'Choisir comme favori (heure de lever sur l\'accueil)'}>
          <IconButton size="small" sx={{ p: 0.25, color: scenario.favori ? 'warning.main' : 'action.active' }} onClick={onFavori}
            aria-label={scenario.favori ? 'Retirer le favori' : 'Choisir comme favori'}>
            {scenario.favori ? <FavoriIcon fontSize="small" /> : <NonFavoriIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Typography variant="subtitle1" noWrap title={scenario.nom} sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>{scenario.nom}</Typography>
        {plusTard && <Chip size="small" color="primary" label="Lever le plus tard" />}
      </Box>
      {horaire ? (
        <>
          {/* Hauteur fixe : les tableaux des cartes restent alignes ; arrivee et marge sur deux lignes. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, height: 44 }}>
            <Typography variant="h4" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formaterHeure(horaire.leverMin)}</Typography>
            <Box sx={{ minWidth: 0, lineHeight: 1.3 }}>
              <Typography variant="body2" color="text.secondary" noWrap sx={{ lineHeight: 1.3 }}>
                lever · arrivée {formaterHeure(horaire.arriveeMin)}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap sx={{ lineHeight: 1.3 }}>
                {scenario.margeMin > 0 ? `marge ${scenario.margeMin} min` : 'sans marge'}
              </Typography>
            </Box>
          </Box>
          <Table size="small">
            <TableBody>
              <LigneHoraireTable libelle="Lever" heure={formaterHeure(horaire.leverMin)} />
              {lignes.map((i, n) => {
                const l = i === null ? null : horaire.lignes[i];
                return l
                  ? <LigneHoraireTable key={n} libelle={l.libelle} duree={formaterDureeHm(l.dureeMin)} heure={formaterHeure(l.finMin)} type={l.type} />
                  : <LigneHoraireTable key={n} libelle="" heure="" type={null} />;
              })}
            </TableBody>
          </Table>
        </>
      ) : (
        <Typography color="text.secondary">Heure de fin de douche ou durée invalide.</Typography>
      )}
    </Paper>
  );
}

function EditeurScenario({ scenario, horaire, onChange, onSupprimer, onDeplacer }: {
  scenario: Scenario;
  horaire: Horaire | null;
  onChange: (s: Scenario) => void;
  onSupprimer?: () => void;
  onDeplacer: (sens: -1 | 1) => void;
}) {
  const modifierEtape = (i: number, maj: Partial<Etape>) =>
    onChange({ ...scenario, etapes: scenario.etapes.map((e, j) => (j === i ? { ...e, ...maj } : e)) });
  const deplacerEtape = (i: number, sens: -1 | 1) => {
    const j = i + sens;
    if (j < 0 || j >= scenario.etapes.length) return;
    const etapes = [...scenario.etapes];
    [etapes[i], etapes[j]] = [etapes[j], etapes[i]];
    onChange({ ...scenario, etapes });
  };
  const douches = scenario.etapes.filter((e) => e.type === 'DOUCHE').length;

  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          label="Nom"
          size="small"
          value={scenario.nom}
          onChange={(ev) => onChange({ ...scenario, nom: ev.target.value })}
          inputProps={{ maxLength: 40 }}
          sx={{ flex: 1 }}
        />
        <IconButton size="small" onClick={() => onDeplacer(-1)} aria-label="Avancer le scénario"><FlecheHautIcon fontSize="small" /></IconButton>
        <IconButton size="small" onClick={() => onDeplacer(1)} aria-label="Reculer le scénario"><FlecheBasIcon fontSize="small" /></IconButton>
        {onSupprimer && (
          <IconButton size="small" onClick={onSupprimer} aria-label="Supprimer le scénario"><DeleteIcon fontSize="small" /></IconButton>
        )}
      </Box>
      <TextField
        label="Marge ajoutée à la séance (min)"
        size="small"
        type="number"
        value={scenario.margeMin}
        onChange={(ev) => onChange({ ...scenario, margeMin: Math.max(0, Number(ev.target.value) || 0) })}
        helperText="Aléas : lumière, circulation…"
        inputProps={{ min: 0, max: 120 }}
      />
      {douches !== 1 && (
        <Alert severity="warning" sx={{ py: 0 }}>Il faut exactement une étape « Douche (référence) ».</Alert>
      )}
      {/* En-tetes des colonnes des etapes (memes largeurs que les champs). */}
      <Box sx={{ display: 'flex', gap: 0.5, mb: -1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 120 }}>Étape</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ width: 150 }}>Type</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ width: 64 }}>Durée (min)</Typography>
        <Box sx={{ width: 58 }} />
      </Box>
      {scenario.etapes.map((e, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <TextField
            size="small"
            value={e.libelle}
            onChange={(ev) => modifierEtape(i, { libelle: ev.target.value })}
            placeholder="Libellé de l'étape"
            inputProps={{ maxLength: 40, 'aria-label': "Libellé de l'étape" }}
            sx={{ flex: 1, minWidth: 120 }}
          />
          <TextField
            select
            size="small"
            value={e.type}
            onChange={(ev) => modifierEtape(i, { type: ev.target.value as TypeEtape })}
            inputProps={{ 'aria-label': 'Type' }}
            sx={{ width: 150, flexShrink: 0 }}
          >
            {TYPES.map((t) => <MenuItem key={t.valeur} value={t.valeur}>{t.libelle}</MenuItem>)}
          </TextField>
          {e.type === 'SEANCE' ? (
            <Tooltip title="Durée de la séance + marge, moins les trajets en courant">
              <Typography variant="body2" sx={{ width: 64, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {horaire ? formaterDureeHm(horaire.seanceMin) : '—'}
              </Typography>
            </Tooltip>
          ) : (
            <TextField
              size="small"
              type="number"
              value={e.dureeMin}
              onChange={(ev) => modifierEtape(i, { dureeMin: Math.max(0, Number(ev.target.value) || 0) })}
              inputProps={{ min: 0, max: 300, 'aria-label': 'Durée (min)' }}
              sx={{ width: 64, flexShrink: 0 }}
            />
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <IconButton size="small" sx={{ p: 0 }} onClick={() => deplacerEtape(i, -1)} aria-label="Monter"><FlecheHautIcon sx={{ fontSize: 16 }} /></IconButton>
            <IconButton size="small" sx={{ p: 0 }} onClick={() => deplacerEtape(i, 1)} aria-label="Descendre"><FlecheBasIcon sx={{ fontSize: 16 }} /></IconButton>
          </Box>
          <IconButton
            size="small"
            onClick={() => onChange({ ...scenario, etapes: scenario.etapes.filter((_, j) => j !== i) })}
            disabled={scenario.etapes.length <= 1}
            aria-label="Supprimer l'étape"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => onChange({ ...scenario, etapes: [...scenario.etapes, { libelle: 'Étape', dureeMin: 10, type: 'NORMALE' }] })}
        disabled={scenario.etapes.length >= 20}
        sx={{ alignSelf: 'flex-start' }}
      >
        Ajouter une étape
      </Button>
      {horaire && (
        <Typography variant="body2" color="text.secondary">
          Lever {formaterHeure(horaire.leverMin)} · arrivée {formaterHeure(horaire.arriveeMin)}
        </Typography>
      )}
    </Paper>
  );
}
